export class SortedMap<K, V> {
    private _entries: { key: K; value: V }[] = []
    private comparator: (a: K, b: K) => number

    constructor(comparator?: (a: K, b: K) => number) {
        this.comparator = comparator ?? ((a, b) => {
            if (a < b) return -1
            if (a > b) return 1
            return 0
        })
    }

    private findIndex(key: K): number {
        let low = 0
        let high = this._entries.length - 1
        while (low <= high) {
            const mid = (low + high) >>> 1
            const cmp = this.comparator(this._entries[mid].key, key)
            if (cmp === 0) return mid
            if (cmp < 0) low = mid + 1
            else high = mid - 1
        }
        return low
    }

    set(key: K, value: V): this {
        const idx = this.findIndex(key)
        if (idx < this._entries.length && this.comparator(this._entries[idx].key, key) === 0) {
            this._entries[idx].value = value
        } else {
            this._entries.splice(idx, 0, { key, value })
        }
        return this
    }

    get(key: K): V | undefined {
        const idx = this.findIndex(key)
        if (idx < this._entries.length && this.comparator(this._entries[idx].key, key) === 0) {
            return this._entries[idx].value
        }
        return undefined
    }

    has(key: K): boolean {
        const idx = this.findIndex(key)
        return idx < this._entries.length && this.comparator(this._entries[idx].key, key) === 0
    }

    delete(key: K): boolean {
        const idx = this.findIndex(key)
        if (idx < this._entries.length && this.comparator(this._entries[idx].key, key) === 0) {
            this._entries.splice(idx, 1)
            return true
        }
        return false
    }

    clear(): void {
        this._entries = []
    }

    get size(): number {
        return this._entries.length
    }

    keys(): IterableIterator<K> {
        return this._entries.map(entry => entry.key)[Symbol.iterator]()
    }

    values(): IterableIterator<V> {
        return this._entries.map(entry => entry.value)[Symbol.iterator]()
    }

    entries(): IterableIterator<[K, V]> {
        return this._entries.map(entry => [entry.key, entry.value] as [K, V])[Symbol.iterator]()
    }

    [Symbol.iterator](): IterableIterator<[K, V]> {
        return this.entries()
    }

    forEach(callback: (value: V, key: K, map: SortedMap<K, V>) => void, thisArg?: any): void {
        for (const { key, value } of this._entries) {
            callback.call(thisArg, value, key, this)
        }
    }

    /**
     * Finds the largest key whose transformed value is <= searchValue.
     * transform(key) should return values in the same sorted order as keys.
     * Returns null if no such key exists.
     */
    findFloorKey(transform: (key: K) => number, searchValue: number): K | null {
        let low = 0
        let high = this._entries.length - 1
        let resultIndex = -1
        while (low <= high) {
            const mid = (low + high) >>> 1
            const transformed = transform(this._entries[mid].key)
            if (transformed === searchValue) {
                resultIndex = mid
                break
            }
            if (transformed < searchValue) {
                resultIndex = mid
                low = mid + 1
            } else {
                high = mid - 1
            }
        }
        return resultIndex >= 0 ? this._entries[resultIndex].key : null
    }
}
